import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaChartBar,
  FaCalendarAlt, 
  FaCheckCircle,
  FaClipboardList,
  FaExclamationTriangle,
  FaFilter,
  FaSignOutAlt,
  FaTimes,
  FaUser,
  FaUsers,
} from "react-icons/fa";

import WorkPlanning from "./WorkPlanning";
import CustomersAndSites from "./CustomersAndSites";
import CompanySettings from "./CompanySettings";
import MonthManagement from "./MonthManagement";
import "./AdminDashboard.css";
import EntriesFilter from "./EntriesFilter";
import EntriesTable from "./EntriesTable";
import UserList from "./UserList";
import { API_URL } from "../../config";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

const AdminDashboard = ({ onLogout }) => {
  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usersError, setUsersError] = useState(null);
  const [message, setMessage] = useState(null);

  const [activeSection, setActiveSection] = useState("uebersicht");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectedEntries, setSelectedEntries] = useState([]);
const [editingEntry, setEditingEntry] = useState(null);
const [editForm, setEditForm] = useState({
  code: "",
  activity: "",
  time: "",
});
const [savingEdit, setSavingEdit] = useState(false);

// Die Einträge werden bereits vom Backend gefiltert.
const filteredEntries = entries;

  const [selectedUser, setSelectedUser] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const currentRole = localStorage.getItem("role") || "user";
  const currentEmail = localStorage.getItem("email") || "";

  const showStatus = useCallback((type, text) => {
    setMessage({ type, text });

    window.setTimeout(() => {
      setMessage(null);
    }, 4000);
  }, []);

    const loadDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setUsersError(null);

    const loadList = async (path, fallbackMessage) => {
      const response = await fetch(`${API_URL}/api/${path}`, {
        headers: getAuthHeaders(),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error || fallbackMessage);
      }

      if (!Array.isArray(data)) {
        throw new Error("Ungültige Serverantwort: " + fallbackMessage);
      }

      return data;
    };

    try {
      const [entriesResult, usersResult] = await Promise.allSettled([
        loadList("entries", "Einträge konnten nicht geladen werden."),
        loadList("users", "Benutzer konnten nicht geladen werden."),
      ]);

      if (entriesResult.status === "fulfilled") {
        setEntries(entriesResult.value);
      } else {
        setError(
          entriesResult.reason?.message ||
          "Einträge konnten nicht geladen werden."
        );
      }

      if (usersResult.status === "fulfilled") {
        setUsers(usersResult.value);
      } else {
        setUsersError(
          usersResult.reason?.message ||
          "Benutzer konnten nicht geladen werden."
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const fetchFilteredEntries = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();

      if (selectedUser) {
        params.append("user", selectedUser);
      }

      if (startDate) {
        params.append("start", `${startDate} 00:00:00`);
      }

      if (endDate) {
        params.append("end", `${endDate} 23:59:59`);
      }

      const queryString = params.toString();
      const url = queryString
        ? `${API_URL}/api/entries?${queryString}`
        : `${API_URL}/api/entries`;

      const response = await fetch(url, {
  headers: getAuthHeaders(),
});
      const data = await response.json().catch(() => []);

      if (!response.ok) {
        throw new Error(data?.error || "Filter konnte nicht angewendet werden.");
      }

      setEntries(Array.isArray(data) ? data : []);
      setSelectedEntries([]);
      setIsFilterModalOpen(false);
      showStatus("success", "Filter wurde angewendet.");
    } catch (filterError) {
      console.error("Filterfehler:", filterError);
      setError(filterError.message || "Filter konnte nicht angewendet werden.");
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = async () => {
    setSelectedUser("");
    setStartDate("");
    setEndDate("");
    setIsFilterModalOpen(false);
    setSelectedEntries([]);
    await loadDashboardData();
  };


const toDatetimeLocal = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  const local = new Date(
    date.getTime() - date.getTimezoneOffset() * 60000
  );

  return local.toISOString().slice(0, 16);
};

const handleEditEntry = (entry) => {
  if (entry.status !== "erfasst") {
    showStatus(
      "error",
      "Gebuchte und archivierte Einträge können nicht bearbeitet werden."
    );
    return;
  }

  setEditForm({
    code: entry.code || "",
    activity: entry.activity || "",
    time: toDatetimeLocal(entry.time),
  });

  setEditingEntry(entry);
};

const closeEditModal = () => {
  if (savingEdit) return;

  setEditingEntry(null);
};

const handleSaveEntry = async (event) => {
  event.preventDefault();

  if (!editingEntry || savingEdit) return;

  if (
    !editForm.code.trim() ||
    !editForm.activity.trim() ||
    !editForm.time
  ) {
    showStatus("error", "Bitte alle Felder ausfüllen.");
    return;
  }

  const date = new Date(editForm.time);

  if (Number.isNaN(date.getTime())) {
    showStatus("error", "Ungültiges Datum.");
    return;
  }

  setSavingEdit(true);

  try {
    const token = localStorage.getItem("token");

    const response = await fetch(
      `${API_URL}/api/entries/${editingEntry.id}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          code: editForm.code.trim(),
          activity: editForm.activity.trim(),
          time: date.toISOString(),
        }),
      }
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        data.error || "Eintrag konnte nicht bearbeitet werden."
      );
    }

    // Aktive Filter nach dem Speichern beibehalten.
    const params = new URLSearchParams();

    if (selectedUser) {
      params.set("user", selectedUser);
    }

    if (startDate) {
      params.set("start", `${startDate} 00:00:00`);
    }

    if (endDate) {
      params.set("end", `${endDate} 23:59:59`);
    }

    const query = params.toString();

    const refreshResponse = await fetch(
      `${API_URL}/api/entries${query ? `?${query}` : ""}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const refreshedEntries = await refreshResponse
      .json()
      .catch(() => null);

    if (!refreshResponse.ok || !Array.isArray(refreshedEntries)) {
      throw new Error(
        "Änderung gespeichert, aber die Liste konnte nicht aktualisiert werden."
      );
    }

    setEntries(refreshedEntries);
    setEditingEntry(null);
    setSelectedEntries([]);

    showStatus(
      "success",
      data.changed === false
        ? "Keine Änderungen erforderlich."
        : "Eintrag erfolgreich bearbeitet."
    );
  } catch (error) {
    console.error("Fehler beim Bearbeiten:", error);
    showStatus("error", error.message);
  } finally {
    setSavingEdit(false);
  }
};

  const statistics = useMemo(() => {
    const today = new Date();
    const todayKey = today.toISOString().slice(0, 10);

    const todaysEntries = entries.filter((entry) => {
      const entryDate = new Date(entry.time);

      if (Number.isNaN(entryDate.getTime())) {
        return false;
      }

      return entryDate.toISOString().slice(0, 10) === todayKey;
    }).length;

    const activeUsers = new Set(
      entries
        .map((entry) => entry.user_id)
        .filter((userId) => userId !== null && userId !== undefined)
    ).size;

    return {
      entries: entries.length,
      users: users.length,
      todaysEntries,
      activeUsers,
    };
  }, [entries, users]);

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div className="admin-brand">
          <img
            src={`${process.env.PUBLIC_URL}/Images/qroof-logo.png`}
            alt="QRoof"
            className="admin-logo"
          />

          <div>
            <p className="admin-eyebrow">QRoof</p>
            <h1>Administration</h1>
          </div>
        </div>

        <div className="admin-header-actions">
          <div className="current-user">
            <div className="current-user-icon">
              <FaUser />
            </div>

            <div>
              <strong>{currentEmail || "Administrator"}</strong>
              <span>{currentRole}</span>
            </div>
          </div>

          <button
            type="button"
            className="admin-logout-button"
            onClick={onLogout}
          >
            <FaSignOutAlt />
            <span>Logout</span>
          </button>
        </div>
</header>

<div className="admin-navigation">
  <nav className="admin-tabs" aria-label="Administrationsbereiche">
    <button
      type="button"
      className={activeSection === "uebersicht" ? "active" : ""}
      onClick={() => setActiveSection("uebersicht")}
      aria-current={
        activeSection === "uebersicht" ? "page" : undefined
      }
    >
      <FaChartBar />
      Übersicht
    </button>

    <button
      type="button"
      className={activeSection === "verwaltung" ? "active" : ""}
      onClick={() => setActiveSection("verwaltung")}
      aria-current={
        activeSection === "verwaltung" ? "page" : undefined
      }
    >
      <FaClipboardList />
      Einträge
    </button>

    <button
      type="button"
      className={activeSection === "benutzer" ? "active" : ""}
      onClick={() => setActiveSection("benutzer")}
      aria-current={
        activeSection === "benutzer" ? "page" : undefined
      }
    >
      <FaUsers />
      Benutzer
    </button>

    <button
      type="button"
      className={activeSection === "monate" ? "active" : ""}
      onClick={() => setActiveSection("monate")}
      aria-current={
        activeSection === "monate" ? "page" : undefined
      }
    >
      <FaCalendarAlt />
      Monatsverwaltung
    </button>
    <button
  type="button"
  className={activeSection === "firma" ? "active" : ""}
  onClick={() => setActiveSection("firma")}
  aria-current={activeSection === "firma" ? "page" : undefined}
>
  Firmeneinstellungen
</button>

<button
  type="button"
  className={activeSection === "kunden" ? "active" : ""}
  onClick={() => setActiveSection("kunden")}
  aria-current={activeSection === "kunden" ? "page" : undefined}
>
  Kunden & Projekte
</button>

<button
  type="button"
  className={activeSection === "planung" ? "active" : ""}
  onClick={() => setActiveSection("planung")}
  aria-current={activeSection === "planung" ? "page" : undefined}
>
  Arbeitsplanung
</button>
  </nav>
</div>

<main className="admin-content">
        {message && (
          <div
            className={`admin-status admin-status-${message.type}`}
            role="status"
          >
            {message.type === "success" ? (
              <FaCheckCircle />
            ) : (
              <FaExclamationTriangle />
            )}

            <span>{message.text}</span>

            <button
              type="button"
              aria-label="Meldung schließen"
              onClick={() => setMessage(null)}
            >
              <FaTimes />
            </button>
          </div>
        )}
{activeSection === "uebersicht" && (
  <>
        <section className="admin-intro">
          <p className="section-label">Übersicht</p>
          <h2>Verwaltung und Benutzer</h2>
          <p>
            Einträge filtern, Arbeitsdaten verwalten und Benutzerkonten
            organisieren.
          </p>
        </section>

        <section className="statistics-grid">
          <article className="stat-card">
            <div className="stat-icon">
              <FaClipboardList />
            </div>

            <div>
              <span>Einträge gesamt</span>
              <strong>{statistics.entries}</strong>
            </div>
          </article>

          <article className="stat-card">
            <div className="stat-icon">
              <FaUsers />
            </div>

            <div>
              <span>Benutzer</span>
              <strong>{statistics.users}</strong>
            </div>
          </article>

          <article className="stat-card">
            <div className="stat-icon">
              <FaCheckCircle />
            </div>

            <div>
              <span>Heute erfasst</span>
              <strong>{statistics.todaysEntries}</strong>
            </div>
          </article>

          <article className="stat-card">
            <div className="stat-icon">
              <FaUser />
            </div>

            <div>
              <span>Aktive Benutzer</span>
              <strong>{statistics.activeUsers}</strong>
            </div>
          </article>
        </section>
  </>
)}
        
{activeSection === "monate" && (
  <section className="admin-panel">
    <MonthManagement onDataChanged={loadDashboardData} />
  </section>
)}
{activeSection === "firma" && <CompanySettings />}
{activeSection === "kunden" && (
  <section className="admin-panel">
    <CustomersAndSites />
  </section>
)}
{activeSection === "planung" && (
  <section className="admin-panel">
    <WorkPlanning />
  </section>
)}
        {activeSection === "verwaltung" && (
          <section className="admin-panel">

<div className="panel-heading">
  <div>
    <p className="section-label">ARBEITSDATEN</p>
    <h2>Eintragsverwaltung</h2>
    <p className="admin-panel-description">
      Mitarbeiter, E-Mail-Adressen, Tätigkeiten und
      Bearbeitungsstatus im Überblick.
    </p>
  </div>

  <span className="panel-count">
    {entries.length} Einträge
  </span>
</div>


            <div className="admin-actions">
              <button
                type="button"
                className="secondary-action"
                onClick={() => setIsFilterModalOpen(true)}
              >
                <FaFilter />
                Filter
              </button>

            </div>

            {loading && (
              <div className="admin-loading">
                <div className="admin-spinner" />
                <p>Daten werden geladen ...</p>
              </div>
            )}

            {!loading && error && (
              <div className="admin-error-state">
                <FaExclamationTriangle />
                <h3>Daten konnten nicht geladen werden</h3>
                <p>{error}</p>

                <button type="button" onClick={loadDashboardData}>
                  Erneut versuchen
                </button>
              </div>
            )}

            {!loading && !error && entries.length === 0 && (
              <div className="admin-empty-state">
                <FaClipboardList />
                <h3>Keine Einträge vorhanden</h3>
                <p>
                  Sobald ein QR-Code gescannt wurde, erscheint der Eintrag
                  hier.
                </p>
              </div>
            )}

            {!loading && !error && entries.length > 0 && (
              <div className="entries-table-wrapper">
<EntriesTable
  entries={filteredEntries}
  selectedEntries={selectedEntries}
  setSelectedEntries={setSelectedEntries}
  onEditEntry={handleEditEntry}
/>
              </div>
            )}
          </section>
        )}

        {activeSection === "benutzer" && (
          <section className="admin-panel">
            <div className="panel-heading">
              <div>
                <p className="section-label">Konten</p>
                <h2>Benutzerverwaltung</h2>
              </div>

              <span className="panel-count">{users.length}</span>
            </div>

{loading ? (
  <p role="status">Benutzer werden geladen …</p>
) : usersError ? (
  <div className="admin-error-state" role="alert">
    <FaExclamationTriangle />
    <h3>Benutzer konnten nicht geladen werden</h3>
    <p>{usersError}</p>
    <button type="button" onClick={loadDashboardData}>
      Erneut versuchen
    </button>
  </div>
) : (
  <div className="user-list-card">
    <UserList
      users={users}
      setUsers={setUsers}
      currentRole={currentRole}
    />
  </div>
)}
          </section>
        )}
      </main>

{editingEntry && (
  <div className="admin-modal-overlay">
    <div
      className="admin-modal admin-edit-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-entry-title"
    >
      <div className="admin-modal-header">
        <div>
          <p className="section-label">Eintragsverwaltung</p>
          <h2 id="edit-entry-title">Eintrag bearbeiten</h2>
        </div>

        <button
          type="button"
          className="admin-modal-close"
          onClick={closeEditModal}
          disabled={savingEdit}
          aria-label="Schließen"
        >
          <FaTimes />
        </button>
      </div>

      <form
        className="admin-edit-form"
        onSubmit={handleSaveEntry}
      >
        <div className="admin-edit-user">
          <FaUser />
          <div>
            <strong>
              {[
                editingEntry.user_first_name,
                editingEntry.user_last_name,
              ]
                .filter(Boolean)
                .join(" ") ||
                editingEntry.user_email ||
                "Mitarbeiter"}
            </strong>

            {editingEntry.user_email && (
              <span>{editingEntry.user_email}</span>
            )}
          </div>
        </div>

        <div className="admin-edit-field">
          <label htmlFor="edit-code">Arbeitsort</label>
          <input
            id="edit-code"
            type="text"
            value={editForm.code}
            maxLength={500}
            onChange={(event) =>
              setEditForm((previous) => ({
                ...previous,
                code: event.target.value,
              }))
            }
            required
          />
        </div>

        <div className="admin-edit-field">
          <label htmlFor="edit-activity">
            Tätigkeitsbeschreibung
          </label>
          <textarea
            id="edit-activity"
            value={editForm.activity}
            rows={5}
            maxLength={1000}
            onChange={(event) =>
              setEditForm((previous) => ({
                ...previous,
                activity: event.target.value,
              }))
            }
            required
          />
        </div>

        <div className="admin-edit-field">
          <label htmlFor="edit-time">
            Datum und Uhrzeit
          </label>
          <input
            id="edit-time"
            type="datetime-local"
            value={editForm.time}
            onChange={(event) =>
              setEditForm((previous) => ({
                ...previous,
                time: event.target.value,
              }))
            }
            required
          />
        </div>

        <div className="admin-edit-notice">
          Änderungen werden im Änderungsverlauf
          des Eintrags protokolliert.
        </div>

        <div className="admin-edit-footer">
          <button
            type="button"
            className="secondary-action"
            onClick={closeEditModal}
            disabled={savingEdit}
          >
            Abbrechen
          </button>

          <button
            type="submit"
            className="filter-apply-button"
            disabled={savingEdit}
          >
            <FaCheckCircle />
            {savingEdit
              ? "Wird gespeichert ..."
              : "Änderungen speichern"}
          </button>
        </div>
      </form>
    </div>
  </div>
)}
      {isFilterModalOpen && (
        <div className="admin-modal-overlay">
          <div
            className="admin-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="filter-title"
          >
            <div className="admin-modal-header">
              <div>
                <p className="section-label">Auswahl eingrenzen</p>
                <h2 id="filter-title">Einträge filtern</h2>
              </div>

              <button
                type="button"
                className="admin-modal-close"
                onClick={() => setIsFilterModalOpen(false)}
                aria-label="Filter schließen"
              >
                <FaTimes />
              </button>
            </div>

            <EntriesFilter
              users={users}
              selectedUser={selectedUser}
              setSelectedUser={setSelectedUser}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
            />

            <div className="filter-footer">
              <button
                type="button"
                className="filter-reset-button"
                onClick={resetFilters}
              >
                Filter zurücksetzen
              </button>

              <button
                type="button"
                className="filter-apply-button"
                onClick={fetchFilteredEntries}
              >
                <FaFilter />
                Anwenden
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;